import { useMemo, useCallback } from 'react';

import { Paper, Button } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { EntityType } from 'enums';
import { FilterTypeEnum } from 'generated';

import { useStyles } from './styles';

interface EntityFilterByTypeProps {
  selectedFilterType?: FilterTypeEnum;
  selectedEntityType?: EntityType;
  loading?: boolean;
  onSelectFilter(filter: FilterTypeEnum): void;
}

type FilterType = {
  id: FilterTypeEnum;
  label: string;
};

const GENERAL_FILTER: FilterType = {
  id: FilterTypeEnum.General,
  label: 'Geral',
};

const BRANDS_FILTER: FilterType = {
  id: FilterTypeEnum.Brands,
  label: 'Marcas',
};

const SCHOOLS_FILTER: FilterType = {
  id: FilterTypeEnum.Schools,
  label: 'Escolas',
};

const filters: Record<EntityType, FilterType[]> = {
  [EntityType.COMPANY]: [GENERAL_FILTER, BRANDS_FILTER, SCHOOLS_FILTER],
  [EntityType.BRAND]: [GENERAL_FILTER, SCHOOLS_FILTER],
  [EntityType.SCHOOL]: [GENERAL_FILTER],
};

export function EntityFilterByType({
  onSelectFilter,
  loading,
  selectedEntityType = EntityType.SCHOOL,
  selectedFilterType = FilterTypeEnum.General,
}: EntityFilterByTypeProps): JSX.Element {
  const classes = useStyles();

  const memoFilterTypes = useMemo(() => {
    return filters[selectedEntityType];
  }, [selectedEntityType]);

  const getButtonVariant = useCallback(
    (filter: FilterTypeEnum) => {
      return filter === selectedFilterType ? 'contained' : 'text';
    },
    [selectedFilterType],
  );

  const getButtonColor = useCallback(
    (filter: FilterTypeEnum) => {
      return filter === selectedFilterType ? 'primary' : 'inherit';
    },
    [selectedFilterType],
  );

  const handleSelectFilter = useCallback(
    (filter: FilterTypeEnum) => () => {
      if (filter === selectedFilterType) {
        return;
      }

      onSelectFilter(filter);
    },
    [onSelectFilter, selectedFilterType],
  );

  return (
    <Paper className={classes.root} elevation={0}>
      {!loading &&
        memoFilterTypes.map(filter => (
          <Button
            disableElevation
            size="small"
            color={getButtonColor(filter.id)}
            className={classes.filterButton}
            key={filter.id}
            variant={getButtonVariant(filter.id)}
            onClick={handleSelectFilter(filter.id)}
          >
            {filter.label}
          </Button>
        ))}
      {loading &&
        memoFilterTypes.map(filter => (
          <Skeleton
            className={classes.skeleton}
            animation="wave"
            key={`${filter.id}-skeleton`}
            height={22}
            width={60}
          />
        ))}
    </Paper>
  );
}
